import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SortIcon } from '@/components/common/SortIcon';
import type { Attendance, AttendanceFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: AttendanceFilters;
}

export function useAttendanceColumns({
  orderBy,
  order,
  onSort,
}: GetColumnsParams): ColumnDef<Attendance>[] {
  const { t } = useTranslation();

  const columns: ColumnDef<Attendance>[] = [
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('name')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('name')}
        >
          <p>{t('name')}</p>
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/attendances/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.createdBy?.name ?? '-'}
        </Link>
      ),
    },
    {
      accessorKey: 'arrivedAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => onSort('arrivedAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('arrivedAt')}
        >
          <p>{t('arrivedAt')}</p>
          <SortIcon active={orderBy === 'arrivedAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const dateStr = row.original.arrivedAt;
        return dateStr ? (
          <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      accessorKey: 'leavedAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => onSort('leavedAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('leavedAt')}
        >
          <p>{t('leavedAt')}</p>
          <SortIcon active={orderBy === 'leavedAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const dateStr = row.original.leavedAt;
        return dateStr ? (
          <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      accessorKey: 'latitude',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => onSort('latitude')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('latitude')}
        >
          <p>{t('latitude')}</p>
          <SortIcon active={orderBy === 'latitude'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.latitude ?? '-'}</div>,
    },
    {
      accessorKey: 'longitude',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => onSort('longitude')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('longitude')}
        >
          <p>{t('longitude')}</p>
          <SortIcon active={orderBy === 'longitude'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.longitude ?? '-'}</div>,
    },
    {
      accessorKey: 'maps',
      header: () => <p>{t('maps')}</p>,
      cell: ({ row }) => {
        const lat = row.original.latitude;
        const lng = row.original.longitude;

        return (
          <Button
            variant="default"
            size="sm"
            onClick={() => window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank')}
            disabled={!(lat && lng)}
          >
            <MapPin className="h-4 w-4 mr-1" />
            {t('map')}
          </Button>
        );
      },
    },
    {
      accessorKey: 'late_reason',
      header: () => <p>{t('lateReason')}</p>,
      cell: ({ row }) => <div>{row.original.late_reason ?? '-'}</div>,
    },
    {
      accessorKey: 'early_reason',
      header: () => <p>{t('earlyReason')}</p>,
      cell: ({ row }) => <div>{row.original.early_reason ?? '-'}</div>,
    },
  ];

  return columns;
}
